/**
 * CourseModulesList Component
 *
 * Displays a list of course modules for self-guided courses.
 * Shows video thumbnails with play/lock icons, module titles, and descriptions.
 *
 * Interactions:
 * - Chevron icon (>) → Toggle expand/collapse of full description
 * - Video thumbnail, title, or description → Play the video in modal
 */

import { ArrowRight, Lock, Music, Play } from "lucide-react"
import { useState } from "react"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import type { CourseModule, RitualMedia } from "../types/detail.types"
import { CourseVideoPlayer } from "./CourseVideoPlayer"
import { RitualAudioPlayer } from "./detail/RitualAudioPlayer"

export interface CourseModulesListProps {
  modules: CourseModule[]
  audioItems?: RitualMedia[]
  audioCoverImageUrl?: string
  courseTitle?: string
  ritualId?: string
  canAccessContent?: boolean
  hideWrapper?: boolean // If true, returns content without wrapper and title (for use inside Collapsible)
}

// Row rendered for an audio item appended after the course modules.
// Mirrors the visual rhythm of ModuleItem (thumbnail tile + title + chevron).
function AudioRow({
  item,
  onPlay,
  canAccessContent,
}: {
  item: RitualMedia
  onPlay: () => void
  canAccessContent?: boolean
}) {
  const isUnlocked = canAccessContent !== false

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg transition-colors">
      <button
        type="button"
        className={`flex items-start gap-3 flex-1 min-w-0 text-left ${
          isUnlocked ? "hover:bg-muted/50 cursor-pointer -m-3 p-3 rounded-lg transition-colors" : ""
        }`}
        onClick={isUnlocked ? onPlay : undefined}
        disabled={!isUnlocked}
        aria-label={`Play audio: ${item.title || "Audio"}`}
      >
        <div className="relative w-20 h-14 flex-shrink-0 rounded overflow-hidden bg-brand-olive/10 flex items-center justify-center">
          {item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <Music className="w-6 h-6 text-brand-olive" />
          )}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            {isUnlocked ? (
              <div className="w-8 h-8 rounded-full bg-brand-terracotta-bright flex items-center justify-center">
                <Play size={15} className="text-white fill-white ml-0.5" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-[rgb(0,0,0,0.8)] flex items-center justify-center">
                <Lock size={15} className="text-white" />
              </div>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{item.title || "Audio"}</p>
          <p className="text-xs text-muted-foreground truncate">Audio</p>
        </div>
      </button>
      <ArrowRight size={16} className="text-muted-foreground flex-shrink-0 mt-3" />
    </div>
  )
}

// Individual module item
function ModuleItem({
  module,
  index,
  isExpanded,
  onToggleExpand,
  onPlayVideo,
  canAccessContent,
}: {
  module: CourseModule
  index: number
  isExpanded: boolean
  onToggleExpand: () => void
  onPlayVideo: () => void
  canAccessContent?: boolean
}) {
  const isUnlocked = canAccessContent && (module.isUnlocked ?? module.isFree)

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg transition-colors">
      {/* Clickable area for video play */}
      <div
        className={`flex items-start gap-3 flex-1 min-w-0 ${
          isUnlocked ? "hover:bg-muted/50 cursor-pointer -m-3 p-3 rounded-lg transition-colors" : ""
        }`}
        onClick={isUnlocked ? onPlayVideo : undefined}
        role={isUnlocked ? "button" : undefined}
        tabIndex={isUnlocked ? 0 : undefined}
        onKeyDown={
          isUnlocked
            ? e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  onPlayVideo()
                }
              }
            : undefined
        }
        aria-label={`Play video: ${module.title}`}
      >
        {/* Thumbnail with play/lock overlay */}
        <div className="relative w-20 h-14 flex-shrink-0 rounded overflow-hidden bg-muted">
          {module.videoUrl ? (
            <video
              src={module.videoUrl}
              className="w-full h-full object-cover"
              muted
              onError={e => {
                const target = e.currentTarget
                target.style.display = "none"
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-brand-olive/10">
              <span className="text-xs text-brand-olive font-semibold">Day {index + 1}</span>
            </div>
          )}

          {/* Play/Lock Icon Overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            {isUnlocked ? (
              <div className="w-8 h-8 rounded-full bg-brand-terracotta-bright flex items-center justify-center">
                <Play size={15} className="text-white fill-white ml-0.5" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-full bg-[rgb(0,0,0,0.8)] flex items-center justify-center">
                <Lock size={15} className="text-white" />
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {module.progressPercent !== undefined && module.progressPercent > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
              <div
                className="h-full bg-brand-terracotta-bright transition-all duration-300"
                style={{ width: `${Math.min(module.progressPercent, 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* Module Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">{module.title}</p>
          <p
            className={`text-xs text-muted-foreground transition-all duration-200 ${
              isExpanded ? "" : "truncate"
            }`}
          >
            {module.description}
          </p>
        </div>
      </div>

      {/* Chevron button for expand/collapse */}
      <button
        type="button"
        className="flex-shrink-0 p-1 rounded hover:bg-muted/50 transition-colors focus:outline-none cursor-pointer"
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? "Collapse description" : "Expand description"}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            onToggleExpand()
          }
        }}
      >
        <ArrowRight
          size={16}
          className={`text-muted-foreground transition-transform duration-200 ${
            isExpanded ? "rotate-90" : ""
          }`}
        />
      </button>
    </div>
  )
}

export function CourseModulesList({
  modules,
  audioItems,
  audioCoverImageUrl,
  ritualId,
  canAccessContent = true,
  hideWrapper = false,
}: CourseModulesListProps) {
  const [selectedModule, setSelectedModule] = useState<CourseModule | null>(null)
  const [isPlayerOpen, setIsPlayerOpen] = useState(false)
  const [selectedAudio, setSelectedAudio] = useState<RitualMedia | null>(null)
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set())

  const hasModules = !!modules && modules.length > 0
  const hasAudio = !!audioItems && audioItems.length > 0

  if (!hasModules && !hasAudio) {
    return null
  }

  // Sort modules by order
  const sortedModules = hasModules ? [...modules].sort((a, b) => a.order - b.order) : []

  const handlePlayVideo = (module: CourseModule) => {
    if (module.isUnlocked ?? module.isFree) {
      setSelectedModule(module)
      setIsPlayerOpen(true)
    }
  }

  const handleClosePlayer = () => {
    setIsPlayerOpen(false)
    setSelectedModule(null)
  }

  const handleToggleExpand = (moduleId: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev)
      if (next.has(moduleId)) {
        next.delete(moduleId)
      } else {
        next.add(moduleId)
      }
      return next
    })
  }

  const content = (
    <div className="space-y-2 max-h-[500px] overflow-y-auto">
      {sortedModules.map((module, index) => (
        <ModuleItem
          key={module.id}
          module={module}
          index={index}
          isExpanded={expandedModules.has(module.id)}
          onToggleExpand={() => handleToggleExpand(module.id)}
          onPlayVideo={() => handlePlayVideo(module)}
          canAccessContent={canAccessContent}
        />
      ))}
      {/* Audio items render after the course days. Same row UI but with a
          music-note thumbnail and a click handler that opens the player in
          a modal — no progress / unlock semantics yet. */}
      {audioItems?.map(item => (
        <AudioRow
          key={item.id}
          item={item}
          canAccessContent={canAccessContent}
          onPlay={() => setSelectedAudio(item)}
        />
      ))}
    </div>
  )

  return (
    <>
      {hideWrapper ? (
        content
      ) : (
        <div className="bg-card rounded-lg p-6">
          <h3 className="text-[16px] font-bold text-foreground uppercase mb-4">Explore Course</h3>
          {content}
        </div>
      )}

      {/* Video Player Modal */}
      {selectedModule && ritualId && (
        <CourseVideoPlayer
          isOpen={isPlayerOpen}
          onClose={handleClosePlayer}
          module={selectedModule}
          ritualId={ritualId}
        />
      )}

      {/* Audio Player Modal */}
      <Dialog
        open={!!selectedAudio}
        onOpenChange={open => {
          if (!open) setSelectedAudio(null)
        }}
      >
        <DialogContent className="max-w-3xl p-0 bg-black border-0 overflow-hidden">
          <DialogTitle className="sr-only">{selectedAudio?.title || "Audio"}</DialogTitle>
          {selectedAudio && (
            <RitualAudioPlayer
              audioUrl={selectedAudio.url}
              coverImageUrl={selectedAudio.thumbnailUrl || audioCoverImageUrl}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
